/**
 * SpendAnalytics — Comprehensive spend analytics page with trending,
 * vendor comparison, anomaly management, cost optimization, and
 * category breakdown.
 *
 * RBAC: Admin, Controller, CFO for anomalies; financial data per Requirement 14.
 *
 * Requirements: 46.2, 46.3, 46.6, 46.9, 46.10, 46.13, 46.14
 */

import { useState, useEffect, useCallback } from 'react';
import {
  spendAnalyticsService,
  SpendTrendItem,
  VendorSpendItem,
  SpendCategoryItem,
  SpendForecastResult,
  SpendAnomaly,
  AnomalyListResult,
  CostOptimizationResult,
} from '@/services/spendAnalyticsService';

// ─── Helpers ───────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function directionArrow(direction: string): string {
  switch (direction) {
    case 'up': return '↑';
    case 'down': return '↓';
    default: return '→';
  }
}

function directionColor(direction: string): string {
  switch (direction) {
    case 'up': return 'text-red-600';
    case 'down': return 'text-green-600';
    default: return 'text-gray-500';
  }
}

function severityBadge(severity: string | null): string {
  switch (severity) {
    case 'High': return 'bg-red-100 text-red-800';
    case 'Medium': return 'bg-yellow-100 text-yellow-800';
    case 'Low': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

type TabId = 'trending' | 'vendors' | 'categories' | 'anomalies' | 'optimization';

// ─── Main Component ────────────────────────────────────────────────────

export function SpendAnalytics() {
  const [activeTab, setActiveTab] = useState<TabId>('trending');

  const tabs: { id: TabId; label: string }[] = [
    { id: 'trending', label: 'Spend Trends' },
    { id: 'vendors', label: 'Vendor Comparison' },
    { id: 'categories', label: 'Category Breakdown' },
    { id: 'anomalies', label: 'Anomalies' },
    { id: 'optimization', label: 'Cost Optimization' },
  ];

  return (
    <div >
      <h1 className="text-lg font-semibold mb-6">Spend Analytics</h1>

      {/* Forecast summary */}
      <ForecastSummary />

      {/* Tab navigation */}
      <div className="border-b mb-6" role="tablist" aria-label="Spend analytics sections">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab panels */}
      <div id={`panel-${activeTab}`} role="tabpanel">
        {activeTab === 'trending' && <SpendTrendingPanel />}
        {activeTab === 'vendors' && <VendorComparisonPanel />}
        {activeTab === 'categories' && <CategoryBreakdownPanel />}
        {activeTab === 'anomalies' && <AnomalyPanel />}
        {activeTab === 'optimization' && <CostOptimizationPanel />}
      </div>
    </div>
  );
}

// ─── Forecast Summary ──────────────────────────────────────────────────

function ForecastSummary() {
  const [forecast, setForecast] = useState<SpendForecastResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    spendAnalyticsService.getSpendForecast()
      .then(setForecast)
      .catch(() => setForecast(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="mb-6 text-slate-400">Loading forecast...</div>;
  if (!forecast) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <SummaryCard label="YTD Actual Spend" value={formatCurrency(forecast.ytdActual)} />
      <SummaryCard label="Projected Annual" value={formatCurrency(forecast.projectedAnnual)} />
      <SummaryCard label="Remaining Projected" value={formatCurrency(forecast.remainingProjected)} />
      <SummaryCard
        label="YoY Trend"
        value={`${directionArrow(forecast.trend)} ${forecast.trend.charAt(0).toUpperCase() + forecast.trend.slice(1)}`}
        valueClass={directionColor(forecast.trend)}
      />
    </div>
  );
}

// ─── Spend Trending Panel ──────────────────────────────────────────────

function SpendTrendingPanel() {
  const [granularity, setGranularity] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [items, setItems] = useState<SpendTrendItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await spendAnalyticsService.getSpendTrending({ granularity });
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trending data.');
    } finally {
      setLoading(false);
    }
  }, [granularity]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    const url = spendAnalyticsService.getSpendTrendingExportUrl({ granularity });
    window.open(url, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="granularity" className="text-sm font-medium">Granularity</label>
          <select
            id="granularity"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as typeof granularity)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-baker-red text-white rounded-lg font-medium hover:bg-baker-red/90 text-sm hover:bg-baker-red/90"
          aria-label="Export spend trending to CSV"
        >
          Export CSV
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && <LoadingIndicator />}

      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-page">
                <th className="border px-3 py-2 text-left">Period</th>
                <th className="border px-3 py-2 text-right">Amount</th>
                <th className="border px-3 py-2 text-right">Previous</th>
                <th className="border px-3 py-2 text-right">Change</th>
                <th className="border px-3 py-2 text-center">Trend</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.period} className="hover:bg-page">
                  <td className="border px-3 py-2">{item.period}</td>
                  <td className="border px-3 py-2 text-right">{formatCurrency(item.amount)}</td>
                  <td className="border px-3 py-2 text-right">{formatCurrency(item.previousAmount)}</td>
                  <td className={`border px-3 py-2 text-right ${directionColor(item.direction)}`}>
                    {item.changePercent >= 0 ? '+' : ''}{item.changePercent.toFixed(1)}%
                  </td>
                  <td className={`border px-3 py-2 text-center text-lg ${directionColor(item.direction)}`}>
                    {directionArrow(item.direction)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <EmptyState message="No spend trending data available." />
      )}
    </div>
  );
}

// ─── Vendor Comparison Panel ───────────────────────────────────────────

function VendorComparisonPanel() {
  const [items, setItems] = useState<VendorSpendItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    spendAnalyticsService.getVendorSpend()
      .then(setItems)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load vendor spend.'))
      .finally(() => setLoading(false));
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (loading) return <LoadingIndicator />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-page">
            <th className="border px-3 py-2 text-left">Vendor</th>
            <th className="border px-3 py-2 text-right">Contracted</th>
            <th className="border px-3 py-2 text-right">Invoiced</th>
            <th className="border px-3 py-2 text-right">Variance</th>
            <th className="border px-3 py-2 text-right">Variance %</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.vendorId} className="hover:bg-page">
              <td className="border px-3 py-2">
                <a href={`/vendors/${item.vendorId}`} className="text-baker-red hover:underline">
                  {item.vendorName}
                </a>
              </td>
              <td className="border px-3 py-2 text-right">{formatCurrency(item.contractedAmount)}</td>
              <td className="border px-3 py-2 text-right">{formatCurrency(item.invoicedAmount)}</td>
              <td className={`border px-3 py-2 text-right ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(item.variance)}
              </td>
              <td className={`border px-3 py-2 text-right ${item.variancePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {item.variancePercent >= 0 ? '+' : ''}{item.variancePercent.toFixed(1)}%
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={5} className="border px-4 py-8 text-center text-slate-500">
                No vendor spend data available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Category Breakdown Panel ──────────────────────────────────────────

function CategoryBreakdownPanel() {
  const [items, setItems] = useState<SpendCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    spendAnalyticsService.getCategoryBreakdown()
      .then(setItems)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load categories.'))
      .finally(() => setLoading(false));
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (loading) return <LoadingIndicator />;

  return (
    <div>
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.category} className="bg-white bg-white rounded-lg border border-border p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{item.category}</span>
                <span className="text-sm text-slate-600">{formatCurrency(item.amount)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-baker-red h-2.5 rounded-full"
                  style={{ width: `${Math.min(item.percentOfTotal, 100)}%` }}
                  role="progressbar"
                  aria-valuenow={item.percentOfTotal}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${item.category}: ${item.percentOfTotal.toFixed(1)}% of total`}
                />
              </div>
              <div className="text-xs text-slate-500 mt-1">{item.percentOfTotal.toFixed(1)}% of total</div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState message="No category breakdown data available." />
      )}
    </div>
  );
}

// ─── Anomaly Panel ─────────────────────────────────────────────────────

function AnomalyPanel() {
  const [result, setResult] = useState<AnomalyListResult | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await spendAnalyticsService.getAnomalies({
        isResolved: showResolved ? undefined : false,
      });
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load anomalies.');
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async (anomalyId: number) => {
    try {
      await spendAnalyticsService.resolveAnomaly(anomalyId, resolveNotes);
      setResolvingId(null);
      setResolveNotes('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve anomaly.');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="rounded"
          />
          Show resolved
        </label>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && <LoadingIndicator />}

      {!loading && result && result.data.length > 0 && (
        <div className="space-y-3">
          {result.data.map((anomaly) => (
            <AnomalyCard
              key={anomaly.anomalyId}
              anomaly={anomaly}
              isResolving={resolvingId === anomaly.anomalyId}
              resolveNotes={resolvingId === anomaly.anomalyId ? resolveNotes : ''}
              onStartResolve={() => { setResolvingId(anomaly.anomalyId); setResolveNotes(''); }}
              onCancelResolve={() => setResolvingId(null)}
              onResolveNotesChange={setResolveNotes}
              onResolve={() => handleResolve(anomaly.anomalyId)}
            />
          ))}
        </div>
      )}

      {!loading && result && result.data.length === 0 && (
        <EmptyState message="No anomalies found." />
      )}
    </div>
  );
}

interface AnomalyCardProps {
  anomaly: SpendAnomaly;
  isResolving: boolean;
  resolveNotes: string;
  onStartResolve: () => void;
  onCancelResolve: () => void;
  onResolveNotesChange: (notes: string) => void;
  onResolve: () => void;
}

function AnomalyCard({
  anomaly,
  isResolving,
  resolveNotes,
  onStartResolve,
  onCancelResolve,
  onResolveNotesChange,
  onResolve,
}: AnomalyCardProps) {
  return (
    <div className={`bg-white rounded-lg border border-border p-5 ${anomaly.isResolved ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityBadge(anomaly.severity)}`}>
            {anomaly.severity}
          </span>
          <span className="text-sm font-medium">{anomaly.anomalyType}</span>
          {anomaly.isResolved && (
            <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">Resolved</span>
          )}
        </div>
        <span className="text-xs text-slate-500">{new Date(anomaly.createdAt).toLocaleDateString()}</span>
      </div>

      {anomaly.recommendedAction && (
        <p className="text-sm text-slate-700 mb-2">{anomaly.recommendedAction}</p>
      )}

      <div className="flex gap-4 text-xs text-slate-500 mb-2">
        {anomaly.expectedAmount !== null && (
          <span>Expected: {formatCurrency(anomaly.expectedAmount)}</span>
        )}
        {anomaly.actualAmount !== null && (
          <span>Actual: {formatCurrency(anomaly.actualAmount)}</span>
        )}
        {anomaly.deviationPercentage !== null && (
          <span>Deviation: {anomaly.deviationPercentage.toFixed(1)}%</span>
        )}
      </div>

      {anomaly.isResolved && anomaly.resolutionNotes && (
        <div className="text-xs text-slate-500 mt-2 p-2 bg-page rounded">
          <strong>Resolution:</strong> {anomaly.resolutionNotes}
        </div>
      )}

      {!anomaly.isResolved && !isResolving && (
        <button
          onClick={onStartResolve}
          className="mt-2 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
          aria-label={`Resolve anomaly ${anomaly.anomalyId}`}
        >
          Resolve
        </button>
      )}

      {isResolving && (
        <div className="mt-3 space-y-2">
          <label htmlFor={`resolve-notes-${anomaly.anomalyId}`} className="block text-sm font-medium">
            Resolution Notes
          </label>
          <textarea
            id={`resolve-notes-${anomaly.anomalyId}`}
            value={resolveNotes}
            onChange={(e) => onResolveNotesChange(e.target.value)}
            className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 text-sm"
            rows={2}
            placeholder="Describe the resolution..."
          />
          <div className="flex gap-2">
            <button
              onClick={onResolve}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              Confirm
            </button>
            <button
              onClick={onCancelResolve}
              className="px-3 py-1 text-sm bg-gray-200 text-slate-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Cost Optimization Panel ───────────────────────────────────────────

function CostOptimizationPanel() {
  const [result, setResult] = useState<CostOptimizationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    spendAnalyticsService.getCostOptimization()
      .then(setResult)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load suggestions.'))
      .finally(() => setLoading(false));
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (loading) return <LoadingIndicator />;
  if (!result) return null;

  return (
    <div>
      {/* AI Disclaimer — Requirement 46.9 */}
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm" role="note">
        {result.disclaimer}
      </div>

      {result.suggestions.length > 0 ? (
        <div className="space-y-3">
          {result.suggestions.map((suggestion, idx) => (
            <div key={idx} className="bg-white rounded-lg border border-border p-5 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityBadge(suggestion.severity)}`}>
                  {suggestion.severity}
                </span>
                <span className="text-xs text-slate-500 uppercase">
                  {suggestion.type.replace(/_/g, ' ')}
                </span>
              </div>
              <p className="text-sm text-slate-700">{suggestion.description}</p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState message="No cost optimization suggestions at this time." />
      )}
    </div>
  );
}

// ─── Shared Components ─────────────────────────────────────────────────

function SummaryCard({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="bg-white bg-white rounded-lg border border-border p-5">
      <div className="text-sm text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-semibold ${valueClass ?? 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded" role="alert">
      {message}
    </div>
  );
}

function LoadingIndicator() {
  return <div className="text-slate-500 py-4">Loading...</div>;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center text-slate-500 py-8">{message}</div>
  );
}
